import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { colors } from 'src/theme';
import { updateFormState, useAppDispatch } from 'src/common';
import { ArrowRightIcon } from '../icons';
import { Flex } from '../grid';
import { Button } from '../buttons';
import { Validation, validationRules } from './validation';

// Definiraj tipove za konfiguraciju polja
export interface FieldConfig {
  name: string;
  component: React.FC<any>;
  componentProps?: { [key: string]: any };
  label?: string;
  validations?: Validation[];
  options?: { label: string; value: string }[];
  disabled?: boolean;
  disabledIf?: (values: { [key: string]: any }) => boolean;
  readOnly?: boolean;
  error?: string;
  showIf?: (values: { [key: string]: any }) => boolean;
  formatValue?: (values: { [key: string]: any }) => string;
  onChange?: (value: any) => void;  // , formValues: { [key: string]: any } moze se dodati kao parametar ako će trebati vrijednosti cijele forme
  onBlur?: () => void;
  afterElement?: React.ReactNode;
}

export interface FormConfig {
  gap?: string | number;
  button?: string;
}

// export const getNestedValue = (obj: any, path: string): any => {
//   return path.split('.').reduce((acc, part) => acc && acc[part], obj);
// };

// const setNestedValue = (obj: any, path: string, value: any) => {
//   const keys = path.split('.');
//   let current = obj;
//   for (let i = 0; i < keys.length - 1; i++) {
//     if (!current[keys[i]]) {
//       current[keys[i]] = {};
//     }
//     current = current[keys[i]];
//   }
//   current[keys[keys.length - 1]] = value;
// };

export interface FormRef {
  getValues: () => { [key: string]: any };
  submitForm: () => void;
  validateForm: (validateFields?: string[]) => boolean;
  setFieldValue: (fieldName: string, value: any, validation?: boolean) => void;
  scrollIntoView?: (topOffsetPx?: number) => void;
}

export interface FormProps {
  fields: FieldConfig[];
  config?: FormConfig;
  initialValues?: { [key: string]: any };
  onSubmit: (values: { [key: string]: any }) => void;
  reduxName?: string;
  // Iskljuci nativnu provjeru preglednika (npr. type="email") i osloni se na vlastitu validaciju
  noValidate?: boolean;
}

export const Form = forwardRef<FormRef, FormProps>(({ fields, initialValues, config, onSubmit, reduxName, noValidate }: FormProps, ref) => {
  const [formValues, setFormValues] = useState<{ [key: string]: any }>(initialValues || {});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  // const [fieldValidity, setFieldValidity] = useState<{ [key: string]: boolean }>(() => {
  //   const initialValidity: { [key: string]: boolean } = {};
  //   fields.forEach((field) => {
  //     if (field.required && !(initialValues && initialValues[field.name])) {
  //       initialValidity[field.name] = false;
  //     }
  //   });
  //   return initialValidity;
  // });

  const dispatch = useAppDispatch();

  const formElementRef = useRef<HTMLFormElement>(null);

  // Hardkodirani useEffect za spremanje vrijednosti forme u Redux zbog potrebe pamcenja stanja forme (merge da se ne izgube polja npr. companyName kad je contractor company)
  useEffect(() => {
    if (reduxName) {
      dispatch(updateFormState({ reduxName, formValues }));
    }
  }, [formValues]);

  const configDefault: FormConfig = {
    gap: 16,
    ...config
  };

  const validateField = (fieldName: string, value: any): string | null => {
    const fieldConfig = fields.find((field) => field.name === fieldName);
    if (fieldConfig && fieldConfig.validations) {
      for (const validation of fieldConfig.validations) {
        const params = validation.params ? Object.keys(validation.params).reduce((acc, key) => {
          acc[key] = formValues[validation.params![key]] !== undefined ? formValues[validation.params![key]] : validation.params![key];
          return acc;
        }, {} as { [key: string]: any }) : undefined;

        if (!validationRules[validation.type](value, params)) {
          return validation.message;
        }
      }
    }
    return null;
  };

  const handleFieldChange = (fieldName: string, value: any, isValid: boolean, onChange?: (value: any) => void) => {
    setFormValues((prevValues) => {
      if (typeof value === 'object' && !Array.isArray(value)) {
        // Ako je value objekt, ažuriraj sve vrijednosti unutar objekta
        return {
          ...prevValues,
          ...value,
        };
      }
      // Inače, ažuriraj samo jednu vrijednost
      return {
        ...prevValues,
        [fieldName]: value,
      };
    });

    if (typeof value === 'object' && !Array.isArray(value)) {
      Object.keys(value).forEach((key) => {
        if (!(key === 'street' && value[key] === '')) {
          const errorMessage = validateField(key, value[key]);
          setErrors((prevErrors) => ({
            ...prevErrors,
            [key]: errorMessage || '',
          }));
        }
      });
    } else {
      const errorMessage = validateField(fieldName, value);
      setErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: errorMessage || '',
      }));
    }

    // Pozovi onChange funkciju ako postoji
    if (onChange) onChange(value);

    // setFormValues((prevValues) => {
    //   const updatedValues = { ...prevValues };
    //   if (typeof value === 'object' && !Array.isArray(value)) {
    //     // Ako je value objekt, ažuriraj sve vrijednosti unutar objekta
    //     for (const key in value) {
    //       setNestedValue(updatedValues, key.startsWith(fieldName) ? key : `${fieldName}.${key}`, value[key]);
    //     }
    //   } else {
    //     // Inače, ažuriraj samo jednu vrijednost
    //     setNestedValue(updatedValues, fieldName, value);
    //   }
    //   return updatedValues;
    // });

    // setFieldValidity((prevValidity) => ({
    //   ...prevValidity,
    //   [fieldName]: isValid,
    // }));
  };

  const handleSubmit = () => {
    const formErrors: { [key: string]: string } = {};
    fields.forEach((field) => {
      const errorMessage = validateField(field.name, formValues[field.name]);
      if (errorMessage) {
        formErrors[field.name] = errorMessage;
      }
    });

    setErrors(formErrors);

    if (Object.keys(formErrors).length === 0) {
      onSubmit(formValues);
    }
  };

  const handleValidateForm = (validateFields: string[] = []) => {
    const formErrors: { [key: string]: string } = {};
    fields.forEach((field) => {
      if (field.showIf && !field.showIf(formValues)) return;
      if (validateFields.length !== 0 && !validateFields.includes(field.name)) return
      const errorMessage = validateField(field.name, formValues[field.name]);
      if (errorMessage) {
        formErrors[field.name] = errorMessage;
      }
    });

    setErrors(formErrors);

    return Object.keys(formErrors).length === 0;
  }

  const handleSetFieldValue = (fieldName: string, value: any, validation = false) => {
    setFormValues((prevValues) => {
      const newValues = { ...prevValues };
      newValues[fieldName] = value;
      return newValues;
    });

    if (validation) {
      const errorMessage = validateField(fieldName, value);
      setErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: errorMessage || '',
      }));
    }
  };

  const scrollIntoViewWithOffset = (topOffsetPx = 120) => {
    const el = formElementRef.current;
    if (!el) return;
    el.style.scrollMarginTop = `${topOffsetPx}px`;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    });
  };

  useImperativeHandle(ref, () => ({
    getValues: () => ({...formValues}),
    setFieldValue: handleSetFieldValue,
    submitForm: handleSubmit,
    validateForm: (validateFields: string[] = []) => handleValidateForm(validateFields),
    scrollIntoView: scrollIntoViewWithOffset
  }));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      ref={formElementRef}
      noValidate={noValidate}
    >
      <Flex vertical gap={configDefault.gap}>
        {fields.map((field) => {
          const shouldShow = field.showIf ? field.showIf(formValues) : true;
          const isDisabled = field.disabledIf ? field.disabledIf(formValues) : field.disabled;

          const inputProps: { [key: string]: any } = {
            label: field.label,
            value: field.formatValue ? field.formatValue(formValues) : formValues[field.name] ?? undefined,
            onChange: (value: any, isValid: boolean) =>
              handleFieldChange(field.name, value, isValid, field.onChange),
            onBlur: field.onBlur,
            // required: field.required,
            id: field.name,
            disabled: isDisabled,
            readOnly: field.readOnly,
            error: errors[field.name],
            options: field.options,
          };

          // Dodaj componentProps ako postoji
          if (field.componentProps) {
            Object.keys(field.componentProps).forEach((key) => {
              const value = field.componentProps![key];
              inputProps[key] = formValues[key] !== undefined ? formValues[key] : value;
            });
          }

          return shouldShow ? (
            <div key={field.name}>
              {React.createElement(field.component, inputProps)}
              {field.afterElement}
            </div>) : null
        })}
      </Flex>
      {configDefault.button && <Button btnType="primary" iconPosition="right" text={configDefault.button} type="submit" style={{ width: '100%' }}>
        <ArrowRightIcon color={colors.white} />
      </Button>}
    </form>
  );
});

